import { Node, mergeAttributes } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { inlineReference, type InlineTarget } from "@/lib/labels/inlineReference";
import { testIds } from "@/testIds";

/**
 * The document's reference index, as the editor knows it: key → target. Null
 * until the first fetch, so a reference is never called dangling just because
 * the index has not arrived.
 */
export interface ReferenceIndexState {
  index: ReadonlyMap<string, InlineTarget> | null;
  /** Bumped on every new index, so each reference repaints. */
  version: number;
}

export const referenceIndexKey = new PluginKey<ReferenceIndexState>("referenceIndex");

/**
 * The commands a reference can be written with. The node keeps the one the
 * author used; before it did, every one of these collapsed to \ref on save.
 */
export const REFERENCE_FORMS = ["ref", "cref", "Cref", "eqref", "autoref", "pageref", "nameref"] as const;
export type ReferenceForm = (typeof REFERENCE_FORMS)[number];

/**
 * The command to write for a reference node's `form` attribute.
 *
 * Validated, not trusted: the attribute comes from stored JSON, and emitting it
 * verbatim would let a corrupt or hand-edited value write any command at all —
 * `\input{…}` included — into the LaTeX. Anything unrecognised is \ref, which
 * is also what every node written before this attribute existed means.
 */
export function referenceForm(form: unknown): ReferenceForm {
  return typeof form === "string" && (REFERENCE_FORMS as readonly string[]).includes(form)
    ? (form as ReferenceForm)
    : "ref";
}

export interface ReferenceOptions {
  HTMLAttributes: Record<string, unknown>;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    reference: {
      /**
       * Insert a cross-reference
       */
      insertReference: (label: string, form?: ReferenceForm) => ReturnType;
      /** Give the editor the document's targets, so references show numbers. */
      setReferenceIndex: (targets: ReadonlyArray<InlineTarget & { key: string }>) => ReturnType;
    };
  }
}

/**
 * TipTap extension for inline cross-references
 * Supports @ref{label} syntax for referencing equations, figures, tables, etc.
 */
export const Reference = Node.create<ReferenceOptions>({
  name: "reference",
  group: "inline",
  inline: true,
  atom: true,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      label: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-ref-label") || "",
        renderHTML: (attributes) => ({
          "data-ref-label": attributes.label,
        }),
      },
      form: {
        default: "ref",
        parseHTML: (element) => referenceForm(element.getAttribute("data-ref-form")),
        renderHTML: (attributes) => ({
          "data-ref-form": referenceForm(attributes.form),
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-type="reference"]',
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const label = node.attrs.label as string;

    // Determine reference type from label prefix
    let displayText = label;
    if (label.startsWith("eq:")) {
      displayText = `Eq. (${label.replace("eq:", "")})`;
    } else if (label.startsWith("fig:")) {
      displayText = `Fig. ${label.replace("fig:", "")}`;
    } else if (label.startsWith("tab:")) {
      displayText = `Table ${label.replace("tab:", "")}`;
    } else if (label.startsWith("sec:")) {
      displayText = `Section ${label.replace("sec:", "")}`;
    } else if (label.startsWith("thm:")) {
      displayText = `Theorem ${label.replace("thm:", "")}`;
    } else if (label.startsWith("def:")) {
      displayText = `Definition ${label.replace("def:", "")}`;
    } else if (label.startsWith("lem:")) {
      displayText = `Lemma ${label.replace("lem:", "")}`;
    }

    return [
      "span",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        "data-type": "reference",
        "data-ref-label": label,
        class: "inline-reference",
        title: `Reference: ${label}`,
      }),
      displayText,
    ];
  },

  /**
   * The editor prints what the PDF will: `table 3`, `(▯)` before a PDF
   * compile, `??` when nothing carries the label (SPEC-references §3). The
   * text is drawn here, not stored: renderHTML below still writes the node
   * for the clipboard and for saving.
   */
  addNodeView() {
    return ({ node, editor }) => {
      const dom = document.createElement("span");
      let current = node;
      const paint = () => {
        const label = current.attrs.label as string;
        const form = referenceForm(current.attrs.form);
        const index = referenceIndexKey.getState(editor.state)?.index ?? null;
        const shown = inlineReference(form, label, index);
        dom.className = "inline-reference";
        dom.setAttribute("data-type", "reference");
        dom.setAttribute("data-testid", testIds.references.inline);
        dom.setAttribute("data-ref-label", label);
        dom.setAttribute("data-ref-form", form);
        dom.setAttribute("data-ref-state", shown.state);
        dom.title = shown.title;
        dom.textContent = shown.text;
      };
      paint();
      return {
        dom,
        update(next) {
          if (next.type !== current.type) return false;
          current = next;
          paint();
          return true;
        },
        ignoreMutation: () => true,
      };
    };
  },

  addCommands() {
    return {
      setReferenceIndex:
        (targets) =>
        ({ tr, dispatch }) => {
          if (dispatch) tr.setMeta(referenceIndexKey, new Map(targets.map((t) => [t.key, t])));
          return true;
        },
      insertReference:
        (label: string, form: ReferenceForm = "ref") =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: { label, form: referenceForm(form) },
          });
        },
    };
  },

  addProseMirrorPlugins() {
    const refRegex = /@ref\{([^}]+)\}/g;

    return [
      new Plugin<ReferenceIndexState>({
        key: referenceIndexKey,
        state: {
          init: () => ({ index: null, version: 0 }),
          apply(tr, prev) {
            const index = tr.getMeta(referenceIndexKey) as ReferenceIndexState["index"] | undefined;
            return index ? { index, version: prev.version + 1 } : prev;
          },
        },
        props: {
          // A node view repaints only when its node or its decorations change.
          // A new index changes neither node, so each reference carries the
          // index version as a decoration — a new version repaints them all.
          decorations(state) {
            const { version } = referenceIndexKey.getState(state)!;
            if (version === 0) return DecorationSet.empty;
            const marks: Decoration[] = [];
            state.doc.descendants((node, pos) => {
              if (node.type.name === "reference") {
                marks.push(Decoration.node(pos, pos + node.nodeSize, { "data-ref-index": String(version) }));
              }
            });
            return DecorationSet.create(state.doc, marks);
          },
        },
      }),
      new Plugin({
        key: new PluginKey("referenceInputRule"),
        props: {
          // Render references in the editor view
          decorations: (state) => {
            const decorations: Decoration[] = [];
            const doc = state.doc;

            doc.descendants((node, pos) => {
              if (node.isText && node.text) {
                const text = node.text;
                let match: RegExpExecArray | null;

                refRegex.lastIndex = 0;
                while ((match = refRegex.exec(text)) !== null) {
                  const start = pos + match.index;
                  const end = start + match[0].length;
                  const label = match[1];

                  decorations.push(
                    Decoration.inline(start, end, {
                      class: "reference-highlight",
                      "data-ref-label": label,
                    })
                  );
                }
              }
            });

            return DecorationSet.create(doc, decorations);
          },
        },
      }),
    ];
  },
});

export default Reference;
